export const MIN_FONT_PX = 10;
export const MAX_FONT_PX = 64;

// Shared with StickyNote so the off-screen measure element matches the real sticky.
export const TEXT_FONT_FAMILY = '"Nunito", "Helvetica Neue", Arial, sans-serif';
export const TEXT_LINE_HEIGHT = 1.3;

/** Returns the rendered height (px) of `text` at `fontSize` inside `width`. */
export type MeasureFn = (text: string, fontSize: number, width: number) => number;

// Singleton off-screen div — reused across all stickies to avoid repeated DOM allocation.
let _measureEl: HTMLDivElement | null = null;

/** Lazily creates (or recovers) the off-screen measurement element. */
function getMeasureEl(): HTMLDivElement {
  if (!_measureEl || !document.body.contains(_measureEl)) {
    _measureEl = document.createElement('div');
    _measureEl.setAttribute('aria-hidden', 'true');
    Object.assign(_measureEl.style, {
      position:     'fixed',
      top:          '-9999px',
      left:         '-9999px',
      visibility:   'hidden',
      pointerEvents:'none',
      fontFamily:   TEXT_FONT_FAMILY,
      fontWeight:   '600',
      lineHeight:   String(TEXT_LINE_HEIGHT),
      wordBreak:    'break-word',
      whiteSpace:   'pre-wrap',
      overflowWrap: 'break-word',
      boxSizing:    'border-box',
      overflow:     'hidden',
    });
    document.body.appendChild(_measureEl);
  }
  return _measureEl;
}

/** Default measure: delegates to the browser's layout engine via the off-screen element. */
export const domMeasure: MeasureFn = (text, fontSize, width) => {
  const el = getMeasureEl();
  el.style.width    = `${Math.max(width, 1)}px`;
  el.style.fontSize = `${fontSize}px`;
  el.innerText      = text;
  return el.scrollHeight;
};

/**
 * Binary-searches for the largest font size (px) at which `text` fits inside
 * `availableWidth × availableHeight`. Short text renders at maxPx; long
 * text wraps and shrinks until everything fits.
 *
 * `measure` is injectable so unit tests can run without a real DOM.
 * `minPx` / `maxPx` allow callers (e.g. text labels) to use different bounds.
 */
export function fitFontSize(
  text: string,
  availableWidth: number,
  availableHeight: number,
  measure: MeasureFn = domMeasure,
  minPx = MIN_FONT_PX,
  maxPx = MAX_FONT_PX,
): number {
  if (!text || !text.trim()) return maxPx;

  let lo   = minPx;
  let hi   = maxPx;
  let best = minPx;

  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (measure(text, mid, availableWidth) <= availableHeight) {
      best = mid;
      lo   = mid + 1;
    } else {
      hi   = mid - 1;
    }
  }

  return best;
}

/**
 * Scales a previously fitted font so the font/content-width ratio stays constant.
 * Used on sticky resize so wrap layout does not reflow — only glyph size changes.
 * Returns a fractional px size (no rounding/clamping) so the ratio stays exact;
 * clamping here would break the scale and clip text when shrinking.
 */
export function scaleFontForWidth(
  prevFontPx: number,
  prevContentW: number,
  newContentW: number,
): number {
  if (!(prevContentW > 0) || !(newContentW > 0) || !Number.isFinite(prevFontPx)) {
    return Number.isFinite(prevFontPx) ? prevFontPx : MIN_FONT_PX;
  }
  return prevFontPx * (newContentW / prevContentW);
}

/**
 * Font size for sticky text: fit at the canonical (default) content box, then
 * scale to `contentW`. Deterministic for the same text + size — remounts and
 * other clients match without persisting fontSize.
 */
export function fontForStickyContent(
  text: string,
  contentW: number,
  baseContentW: number,
  baseContentH: number,
  measure: MeasureFn = domMeasure,
): number {
  const fitted = fitFontSize(text, baseContentW, baseContentH, measure);
  return scaleFontForWidth(fitted, baseContentW, contentW);
}
